/**
 * @file `DeliveryPill` — per-recipient delivery-state lozenge for the
 *       campaign detail table.
 * @module @labcraft/admin/ui/emails/delivery-pill
 *
 * Collapses a recipient's send outcome + Resend-webhook delivery state
 * into one labelled pill with a tone. Precedence is worst-first so an
 * operator scanning the column sees problems (complained / bounced /
 * failed) before successes — `clicked` outranks `delivered` (strongest
 * positive first; click is the kept engagement signal, #1690 Part 4,
 * since open tracking is off). Colors come from the
 * `--delivery-<tone>-{bg|fg}` tokens in `emails.css`.
 */

import type { JSX } from 'react'
import type { CampaignRecipient } from '../../shared/emails_routes.ts'

/** Visual tone buckets for a delivery pill. */
export type Tone = 'ok' | 'bad' | 'warn' | 'sent' | 'idle'

/**
 * Reduce a recipient record to a display `{ label, tone }`, worst-first.
 * Exported so the campaign-detail tiles + filter chips bucket recipients by
 * the same collapsed state the pill shows.
 *
 * @param r - The per-recipient delivery record.
 * @returns The pill label + tone.
 */
export function deliveryFor(r: CampaignRecipient): {
  label: string
  tone: Tone
} {
  if (r.sendStatus === 'failed') return { label: 'failed', tone: 'bad' }
  if (r.deliveryState === 'complained') {
    return { label: 'complained', tone: 'bad' }
  }
  if (r.deliveryState === 'bounced') return { label: 'bounced', tone: 'bad' }
  if (r.clickedAt) return { label: 'clicked', tone: 'ok' }
  if (r.deliveryState === 'delivered') return { label: 'delivered', tone: 'ok' }
  if (r.deliveryState === 'delivery_delayed') {
    return { label: 'delayed', tone: 'warn' }
  }
  if (r.deliveryState === 'sent' || r.sendStatus === 'sent') {
    return { label: 'sent', tone: 'sent' }
  }
  return { label: 'pending', tone: 'idle' }
}

/**
 * Render the delivery-state pill for one recipient.
 *
 * @param props.recipient - The per-recipient delivery record.
 */
export function DeliveryPill({
  recipient,
}: {
  recipient: CampaignRecipient
}): JSX.Element {
  const { label, tone } = deliveryFor(recipient)
  return <span className={`delivery-pill delivery-pill--${tone}`}>{label}</span>
}
